// Bottom nav for mobile. Hidden on desktop (≥ sm: breakpoint) where a
// sidebar would take over (sidebar = future enhancement). The center "Add"
// button is the FAB-equivalent for the BottomNav: emphasized, brand color.
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useI18n } from '../i18n/index.jsx';

const items = [
  { to: '/',           labelKey: 'nav.dashboard',     icon: HomeIcon },
  { to: '/transactions', labelKey: 'nav.transactions', icon: ListIcon },
  { to: '/add',        labelKey: 'add.newTitle',      icon: PlusIcon, primary: true },
  { to: '/budgets',    labelKey: 'nav.budgets',       icon: WalletIcon },
  { to: '/settings',   labelKey: 'nav.settings',      icon: GearIcon },
];

export default function BottomNav() {
  const { t } = useI18n();
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-30 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 pb-[env(safe-area-inset-bottom)] sm:hidden"
      aria-label={t('bottomnav.aria')}
    >
      <ul className="flex justify-around items-stretch h-14">
        {items.map(({ to, labelKey, icon: Icon, primary }) => (
          <li key={to} className="flex-1 flex">
            <NavLink to={to} end={to === '/'} className="flex-1 flex">
              {({ isActive }) => (
                <NavContent to={to} label={t(labelKey)} Icon={Icon} primary={primary} isActive={isActive} />
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function NavContent({ to, label, Icon, primary, isActive }) {
  const baseClass = 'flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] min-w-[44px] min-h-[44px]';
  const textClass = primary
    ? 'text-white'
    : isActive ? 'text-brand-600 dark:text-brand-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200';
  return (
    <span className={`${baseClass} ${textClass}`} aria-label={label}>
      {primary ? (
        <span
          className={[
            '-mt-5 w-12 h-12 rounded-full flex items-center justify-center shadow-lg',
            isActive ? 'bg-brand-700' : 'bg-brand-600',
          ].join(' ')}
          aria-hidden="true"
        >
          <Icon className="w-5 h-5" />
        </span>
      ) : (
        <Icon className="w-5 h-5" />
      )}
      <span className={primary ? 'sr-only' : ''}>{label}</span>
    </span>
  );
}

// ── Inline SVG icons (no font dependency). 24×24 viewBox, currentColor. ──
function HomeIcon(p) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 12l9-9 9 9" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}
function ListIcon(p) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <line x1="8" y1="6"  x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <circle cx="4" cy="6"  r="1" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="4" cy="18" r="1" />
    </svg>
  );
}
function PlusIcon(p) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5"  y1="12" x2="19" y2="12" />
    </svg>
  );
}
function WalletIcon(p) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
      <circle cx="17" cy="15" r="1.2" fill="currentColor" />
    </svg>
  );
}
function GearIcon(p) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}
